import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, Empty, Field, Input, Loading, Modal, Pill, Select, Table } from "@/components/Ui";

const LEVEL_LABEL = { admin: "Company administrator", member: "Team member" };

export default function SupplierTeam() {
  const [data, setData] = useState(null);
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/supplier/team").then((r) => setData(r.data)).catch(() => setData({ members: [], invites: [] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  if (!data) return <Loading label="Loading your team…" />;

  const send = async () => {
    setBusy("invite");
    try {
      await api.post("/supplier/team/invites", invite);
      toast.success(`Invitation emailed to ${invite.email}`);
      setInvite(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const act = async (fn, id, label) => {
    setBusy(id);
    try { await fn(); toast.success(label); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="supplier-team">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#147D78]">
              Team accounts
            </p>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tight text-[#102A43]">
              {data.members?.length || 0} user{data.members?.length === 1 ? "" : "s"} on this company
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-[#667788]">
              Colleagues you invite work under the same company account — they see the same matching
              enquiries and can submit quotations. Company administrators can also manage the team
              and the subscription.
            </p>
          </div>
          {data.can_manage ? (
            <Btn data-testid="open-invite-btn" onClick={() => setInvite({ email: "", name: "", level: "member" })}>
              <UserPlus className="h-4 w-4" /> Invite a colleague
            </Btn>
          ) : (
            <Pill tone="warn" testId="team-locked-pill">
              {data.team_enabled ? "Administrator access required" : "Enterprise plan required"}
            </Pill>
          )}
        </div>
        {!data.team_enabled && (
          <p className="mt-4 rounded-[10px] border border-[#f2e2bd] bg-[#fdf7e9] p-3 text-sm text-[#7a5a12]"
            data-testid="team-upgrade-note">
            Team accounts are part of the Enterprise plan. Upgrade from the Subscription page to
            invite colleagues.
          </p>
        )}
      </Card>

      <h3 className="mt-8 font-display text-base font-bold tracking-tight text-[#102A43]">Members</h3>
      <div className="mt-3">
        <Table testId="team-members-table" head={["Name", "Email", "Access level", "Status", ""]}>
          {(data.members || []).map((m) => (
            <tr key={m.id} data-testid={`team-member-${m.id}`}>
              <td className="font-semibold text-[#102A43]">{m.name || "—"}</td>
              <td>{m.email}</td>
              <td>
                {data.can_manage ? (
                  <Select data-testid={`member-level-${m.id}`} value={m.company_role}
                    options={[["member", "Team member"], ["admin", "Company administrator"]]}
                    onChange={(e) => act(() => api.put(`/supplier/team/members/${m.id}/level`,
                      { level: e.target.value }), m.id, "Access level updated")} />
                ) : LEVEL_LABEL[m.company_role]}
              </td>
              <td>
                <Pill tone={m.is_active ? "ok" : "neutral"} testId={`member-status-${m.id}`}>
                  {m.is_active ? "active" : "deactivated"}
                </Pill>
              </td>
              <td>
                {data.can_manage && (
                  <Btn variant={m.is_active ? "danger" : "ghost"} loading={busy === m.id}
                    data-testid={`toggle-member-${m.id}`} className="px-3 py-2 text-xs"
                    onClick={() => act(() => api.put(`/supplier/team/members/${m.id}/active`,
                      { is_active: !m.is_active }), m.id,
                      m.is_active ? "Member deactivated" : "Member reactivated")}>
                    {m.is_active ? "Deactivate" : "Reactivate"}
                  </Btn>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </div>

      <h3 className="mt-8 font-display text-base font-bold tracking-tight text-[#102A43]">
        Pending invitations
      </h3>
      {(data.invites || []).length === 0 ? (
        <Empty testId="team-invites-empty" title="No open invitations"
          sub="Invitations expire automatically after 7 days." />
      ) : (
        <div className="mt-3">
          <Table testId="team-invites-table" head={["Email", "Name", "Level", "Expires", ""]}>
            {data.invites.map((i) => (
              <tr key={i.id} data-testid={`team-invite-${i.id}`}>
                <td className="font-semibold text-[#102A43]">{i.email}</td>
                <td>{i.name || "—"}</td>
                <td>{LEVEL_LABEL[i.level]}</td>
                <td>{i.expires_at ? new Date(i.expires_at).toLocaleDateString() : "—"}</td>
                <td>
                  {data.can_manage && (
                    <Btn variant="danger" className="px-3 py-2 text-xs" loading={busy === i.id}
                      data-testid={`revoke-invite-${i.id}`}
                      onClick={() => act(() => api.delete(`/supplier/team/invites/${i.id}`), i.id,
                        "Invitation revoked")}>
                      Revoke
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </div>
      )}

      {invite && (
        <Modal title="Invite a colleague" onClose={() => setInvite(null)}>
          <div className="grid gap-3">
            <Field label="Work email">
              <Input data-testid="invite-email-input" type="email" value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            </Field>
            <Field label="Full name">
              <Input data-testid="invite-name-input" value={invite.name}
                onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            </Field>
            <Field label="Access level" hint="Administrators can manage the team and the subscription.">
              <Select data-testid="invite-level-select" value={invite.level}
                options={[["member", "Team member"], ["admin", "Company administrator"]]}
                onChange={(e) => setInvite({ ...invite, level: e.target.value })} />
            </Field>
          </div>
          <div className="mt-5 flex gap-3">
            <Btn data-testid="send-invite-btn" loading={busy === "invite"} onClick={send}
              disabled={!invite.email || !invite.name}>
              Send invitation
            </Btn>
            <Btn variant="ghost" data-testid="cancel-invite-btn" onClick={() => setInvite(null)}>
              Cancel
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
